import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  MillableClassification,
  DemandArchetype,
  ClassificationStatus,
} from '@erp/rpc';

export type { MillableClassification, DemandArchetype, ClassificationStatus };

export function useMillableClassifications(
  input: {
    status?: ClassificationStatus;
    archetype?: DemandArchetype;
    productV3Key?: string;
  } = {},
  enabled = true,
) {
  return useQuery({
    ...orpc.procurement.millableClassifications.list.queryOptions({ input }),
    enabled,
  });
}

export function useMillableClassificationForProduct(
  productV3Key: string | undefined,
  enabled = true,
) {
  return useQuery({
    ...orpc.procurement.millableClassifications.getForProduct.queryOptions({
      input: { productV3Key: productV3Key ?? '' },
    }),
    enabled: enabled && !!productV3Key,
  });
}

function invalidateMillable(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({
    queryKey: orpc.procurement.millableClassifications.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.accountsReceivable.salesOrders.key(),
  });
  void queryClient.invalidateQueries({
    queryKey: orpc.procurement.replenishmentSuggestions.key(),
  });
}

export function useScoreMillableClassifications() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.millableClassifications.score.mutationOptions({
      onSuccess: () => invalidateMillable(queryClient),
    }),
  );
}

export function useConfirmMillableClassification() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.millableClassifications.confirm.mutationOptions({
      onSuccess: () => invalidateMillable(queryClient),
    }),
  );
}

export function useRejectMillableClassification() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.millableClassifications.reject.mutationOptions({
      onSuccess: () => invalidateMillable(queryClient),
    }),
  );
}

export function useUpdateLineMaxJoins() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.millableClassifications.updateLineMaxJoins.mutationOptions(
      {
        onSuccess: () => invalidateMillable(queryClient),
      },
    ),
  );
}
